
import { useUniversalEffect } from '../use-universal-effect';
import { useHistory } from '../use-history';
import { useStore } from '../../store/store';
import isBrowser from 'is-browser';
import { getCarlineData } from '../../tagging/tagging-helpers';
import {
  isGuideRoute,
  getModelYearFromHistory,
  getModelIdFromHistory,
  getSectionFromPath,
  normalizeSectionId,
  getSearchPageFromQuery,
  getQueryParams,
  getSearchTermFromQuery,
  normalizeQueryToParams,
  paramsToSearchString,
} from '../../utils/query-param-helpers';
import { useFeatureServices } from '../use-feature-services';
import { useCallback, useEffect } from 'react';
import { createFeatureHubHref, pushWithPathname } from '../../utils/navigation-url';
import { BreadcrumbV1 } from '@volkswagen-onehub/breadcrumb-service';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import { findFirstIndexableSection } from '../../utils/navigation';

interface UseDynamicMetadataProps {
  hasInit: boolean;
}

const formatModelName = (id: string): string => {
  return id
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const cleanKeywordPlaintext = (text: string | null | undefined): string => {
  if (!text) return '';
  return text.replace(/\n/g, '').trim();
};

export const updateMetaTag = (
  attributeType: 'name' | 'property',
  attributeValue: string,
  content: string
): void => {
  if (!isBrowser || typeof document === 'undefined') {
    return;
  }

  let metaTag = document.querySelector(
    `meta[${attributeType}="${attributeValue}"]`
  );
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attributeType, attributeValue);
    document.head.appendChild(metaTag);
  }
  metaTag.setAttribute('content', content);
};

const globalKeywords = [    
  'Volkswagen',
  'Volkswagen Car',
  'Volkswagen suv',
  'Quick Start Guide'
];

export const useDynamicMetadata = ({ hasInit }: UseDynamicMetadataProps) => {
  const history = useHistory();
  const [state] = useStore();
  const { qsgNavigation, qsgSectionMap, qsgSearchResultsMap } = state.qsg;

  const { 
    'page-info-service': pageInfoService, 
    'breadcrumb-service': breadcrumbService, 
    's2:server-request': serverRequest 
  } = useFeatureServices();

  const updateMetadata = useCallback(() => {
    if (!pageInfoService) return;

    const serverRequestUrl = serverRequest?.url;
    const modelYear = getModelYearFromHistory(history);
    const modelId = getModelIdFromHistory(history);

    let title: string;
    let description: string;
    let keywords: string[] = [];
    //let shouldNoIndex = false;

    const isOnGuideRoute = isGuideRoute(history.location.pathname);

    let breadcrumbs: BreadcrumbV1[] = [];
    if (!isOnGuideRoute) {
      // Model selector page
      title = 'Select your VW Quick Start Guide | Volkswagen';
      description = 'Get your Volkswagen car, SUV, or electric vehicle\'s Quick Start Guide by entering your model and model year here.';
    } else if (modelId && modelYear && qsgNavigation) {
      // Guide route with model selected
      const carlineData = getCarlineData(modelId);
      const modelName =
        qsgNavigation?.modelName ||
        carlineData?.MetadataName ||
        formatModelName(modelId);

      const isDesktop =
        isBrowser &&
        typeof window !== 'undefined' &&
        window.innerWidth >= Breakpoints.b1280;
      
      const sections = qsgNavigation?.sections ?? [];
      const defaultSection = isDesktop ? findFirstIndexableSection(sections) : null;
      const defaultSectionId = defaultSection
        ? normalizeSectionId(defaultSection.sectionId)
        : null;
      
      let modelPath = `/${modelYear}/${modelId}`;
      if (defaultSectionId) 
        modelPath = `${modelPath}/${defaultSectionId}`;
      
      const modelHref = createFeatureHubHref(history, modelPath, serverRequestUrl);
      breadcrumbs.push({
        label: `${modelYear} ${modelName}`,
        href: modelHref || '#',
        onClick: () => {
          const query = getQueryParams(history);
          const params = normalizeQueryToParams(query);
          params['model-year'] = undefined;
          params.model = undefined;
          params.search = undefined;
          params.page = undefined;
          const searchString = paramsToSearchString(params);
          pushWithPathname(history, modelPath, searchString);
        },
      })

      // Check if there's a section in the path
      const query = getQueryParams(history);
      const searchTerm = getSearchTermFromQuery(query);
      const searchPage = getSearchPageFromQuery(query) || 1;
      const sectionId = getSectionFromPath(history.location.pathname);
      const normalizedSectionId = normalizeSectionId(sectionId);
      const currentSection = normalizedSectionId
        ? qsgSectionMap[normalizedSectionId]
        : null;
      const sectionName = currentSection?.sectionName;

      if (searchTerm) {
        const searchKey = `${searchTerm}:${searchPage}`;
        const searchResults = qsgSearchResultsMap[searchKey];
        const totalCount = searchResults?.totalCount ?? 0;

        if (totalCount === 0) {
          //shouldNoIndex = true;
          title = `${modelName} ${modelYear} QSG - No results for term: ${searchTerm}`;
          description = `No results available for "${searchTerm}" in our Volkswagen ${modelName} ${modelYear}. Quick Start Guide`;
        } else {
          title = `${modelName} ${modelYear} QSG - Search results Page ${searchPage} for term: ${searchTerm}`;
          description = `Looking for information about ${searchTerm} for your Volkswagen ${modelName} ${modelYear}? View results from the Quick Start Guide here.`;
        }

        const searchPath = `/${modelYear}/${modelId}`;
        const searchHref = createFeatureHubHref(history, `${searchPath}?search=${searchTerm}&page=${searchPage}`, serverRequestUrl);
        breadcrumbs.push({
          label: `Search results Page ${searchPage} for term: "${searchTerm}"`,
          href: searchHref || '#',
          onClick: () => {
            const query = getQueryParams(history);
            const params = normalizeQueryToParams(query);
            params.search = searchTerm;
            params.page = searchPage.toString();
            const searchString = paramsToSearchString(params);
            pushWithPathname(history, `/${modelYear}/${modelId}`, searchString);
          },
        })
      } else if (sectionName) {
        title = `${modelName} ${modelYear} QSG - ${sectionName} | Volkswagen`;
        description = currentSection?.sectionDescription || `Explore how to use and adjust the ${sectionName} in your Volkswagen ${modelName} ${modelYear} with our Quick Start guide here.`;
        keywords.push(...currentSection?.sectionKeywords
          ?.map((keyword) => cleanKeywordPlaintext(keyword?.plaintext))
          .filter((keyword) => keyword.length > 0) || []);

        const normalizedSectionId = normalizeSectionId(sectionId);
        const sectionPath = `/${modelYear}/${modelId}/${normalizedSectionId}`;
        const sectionHref = createFeatureHubHref(history, sectionPath, serverRequestUrl);
        breadcrumbs.push({
          label: sectionName,
          href: sectionHref || '#',
          onClick: () => {
            const query = getQueryParams(history);
            const params = normalizeQueryToParams(query);
            params.search = undefined;
            params.page = undefined;
            const searchString = paramsToSearchString(params);
            pushWithPathname(history, sectionPath, searchString);
          },
        })
      } else {
        title = `${modelName} ${modelYear} Quick Start Guide | Volkswagen`;
        description = `Get the Volkswagen ${modelName} ${modelYear}’s Quick Start Guide. Learn how to use your vehicle’s navigation & safety features, IQ.Drive and more here.`;
      }
      if (!sectionName && !searchTerm){
        keywords.push('vw iq','vw safety','vw cruise control');
      }
      keywords.push(modelName);
      keywords.push(modelYear);
      if (searchTerm)
        keywords.push(searchTerm);
      else if (sectionName)
        keywords.push(sectionName);
    } else {
      // No model selected yet - keep defaults
      return;
    }

    keywords = [...globalKeywords, ...keywords];

    if (breadcrumbService) breadcrumbService.set(breadcrumbs);

    pageInfoService.setPageInfo({
      seo: {
        pageTitle: title,
        description: description,
        og: {
          ogTitle: title,
          ogDescription: description,
        },
        twitter: {
          twitterTitle: title,
          twitterDescription: description,
        },
        ...(keywords.length > 0 ? { keywords: keywords.join(', ') } : {}),
        // TODO: Add no-index tag
      },
    });

    /* CSR Only
    if (!isBrowser || typeof document === 'undefined') return;
    document.title = title;
    updateMetaTag('name', 'description', description);

    // Update AEM CMS metadata tags
    updateMetaTag('property', 'VW.gsaTitle', title);
    updateMetaTag('property', 'VW:gsaTitle', title);
    updateMetaTag('property', 'og:title', title);
    updateMetaTag('property', 'og:description', description);

    updateMetaTag('name', 'twitter:title', title);
    updateMetaTag('name', 'twitter:description', description);
    */
  }, [
    hasInit,
    history,
    qsgNavigation,
    qsgSectionMap,
    qsgSearchResultsMap,
    pageInfoService,
    breadcrumbService,
    serverRequest,
  ]);

  useUniversalEffect(() => {
    updateMetadata();
  }, [updateMetadata]);

  useEffect(() => {
    if (!hasInit) return;

    const unlisten = history.listen(() => {
      updateMetadata();
    });

    return unlisten;
  }, [hasInit, history, updateMetadata]);
};
